import React, { useState, useEffect } from 'react';
import styles from './LocationSearch.module.scss';
import { ReactComponent as CrosshairIcon } from 'assets/crosshair.svg';
import { ReactComponent as CloseIcon } from 'assets/close.svg';
import { useDebouncedState } from 'helpers/useDebouncedState';
import useExternalApiService from 'helpers/ExternalApiService';

interface LocationSuggestion {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface LocationSearchProps {
  selectedLocation: {
    description: string;
    place_id: string;
    lat?: number;
    lng?: number;
  } | null;
  onLocationSelect: (location: any) => void;
}

const LocationSearch: React.FC<LocationSearchProps> = ({ 
  selectedLocation, 
  onLocationSelect 
}) => {
  const [debouncedSearchTerm, searchTerm, setSearchTerm] = useDebouncedState('');
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const { postData } = useExternalApiService(process.env.REACT_APP_EAI_URL);

  // Get current location on mount
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentLocation({
          description: "Current Location",
          place_id: "current_location",
          lat,
          lng
        });
        
        // Don't auto-select location on mount - let user choose
        // This prevents auto-searching on page load
      }, (error) => {
        console.log("Could not get location:", error);
      });
    }
  }, []);

  // Search for location suggestions
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length > 2) {
      searchLocations(debouncedSearchTerm);
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const searchLocations = async (query: string) => {
    try {
      // Use the Google Places API to search for locations (not establishments)
      const result = await postData('/api/google_places_autocomplete', {
        query: query,
        types: '(regions)' // This will search for cities, states, countries
        // Omit location field entirely for location searches (no bias)
      });

      if (result.success && result.places) {
        // Filter out establishment results, keep only geographic locations
        const locationSuggestions = result.places.filter((place: any) => 
          !place.types?.includes('establishment') && 
          !place.types?.includes('point_of_interest')
        );
        setSuggestions(locationSuggestions);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setSuggestions([]);
    }
  };

  const handleLocationSelect = async (suggestion: LocationSuggestion) => {
    // Get full details including coordinates
    try {
      const details = await postData('/api/google_places_details', {
        place_id: suggestion.place_id,
      });

      if (details && details.place && details.place.geometry) {
        onLocationSelect({
          description: suggestion.description,
          place_id: suggestion.place_id,
          lat: details.place.geometry.location.lat,
          lng: details.place.geometry.location.lng
        });
      } else {
        onLocationSelect({
          description: suggestion.description,
          place_id: suggestion.place_id
        });
      }

      setSearchTerm('');
      setIsOpen(false);
      setSuggestions([]);
    } catch (error) {
      console.error('Error getting location details:', error);
    }
  };

  const handleCurrentLocationSelect = () => {
    if (currentLocation) {
      onLocationSelect(currentLocation);
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  const clearLocation = () => {
    if (currentLocation) {
      onLocationSelect(currentLocation);
    }
    setSearchTerm('');
  };

  return (
    <div className={styles.locationSearchContainer}>
      <div className={styles.locationLabel}>Search near:</div>
      <div className={styles.locationInputWrapper}>
        <CrosshairIcon className={styles.locationIcon} width={16} height={16} />
        <input
          type="text"
          className={styles.locationInput}
          placeholder={selectedLocation?.description || "Current Location"}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {searchTerm && (
          <CloseIcon 
            className={styles.clearIcon} 
            width={20} 
            height={20}
            onClick={clearLocation}
          />
        )}
      </div>

      {isOpen && (searchTerm || (!searchTerm && currentLocation)) && (
        <div className={styles.suggestionsDropdown}>
          {!searchTerm && currentLocation && (
            <div 
              className={styles.suggestionItem}
              onClick={handleCurrentLocationSelect}
            >
              <CrosshairIcon width={16} height={16} />
              <span>Current Location</span>
            </div>
          )}
          
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className={styles.suggestionItem}
              onClick={() => handleLocationSelect(suggestion)}
            >
              <div className={styles.suggestionText}>
                {suggestion.structured_formatting ? (
                  <>
                    <div className={styles.mainText}>
                      {suggestion.structured_formatting.main_text}
                    </div>
                    <div className={styles.secondaryText}>
                      {suggestion.structured_formatting.secondary_text}
                    </div>
                  </>
                ) : (
                  <div className={styles.mainText}>{suggestion.description}</div>
                )}
              </div>
            </div>
          ))}
          
          {searchTerm && suggestions.length === 0 && debouncedSearchTerm && (
            <div className={styles.noResults}>
              No locations found for "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;